import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  checkTrajectoryHasLock,
  lockTrajectory,
  unlockTrajectory,
} from '../api/trajectoryApiSlice';
import { store } from 'src/modules/shared/store';
import {
  ITrajectoryDetail,
  TrajectoryLockStatus,
  TrajectorySectionCode,
  TrajectoryViewMode,
} from 'src/config/api/models/Trajectory';

export interface ITrajectoryState {
  currentTrajectoryId?: string;
  trajectory?: ITrajectoryDetail;
  currentBookId?: string;
  currentBlockId?: string;
  currentChapterId?: string;
  currentSectionCode?: TrajectorySectionCode;
  currentPageId?: string;
  viewMode: TrajectoryViewMode;
  lockStatus?: TrajectoryLockStatus;
  timeoutAction?: number;
  loading?: boolean;
  error?: boolean;
  success?: boolean;
  confirmationTrajectory?: ConfirmationTrajectory | null;
  confirmPublishDraft?: boolean;
  confirmUnPublishDraft?: boolean;
  manual?: {
    userHasOpened: boolean;
    exists: boolean;
  };
}

type ConfirmationTrajectory = {
  id: string;
  title?: string;
};

const initialState: ITrajectoryState = {
  viewMode: 'read',
};

const trajectorySlice = createSlice({
  name: 'trajectories',
  initialState,
  reducers: {
    setCurrentTrajectoryId: (state, action: PayloadAction<string>) => {
      if (state.currentTrajectoryId !== action.payload) {
        state.currentTrajectoryId = action.payload;
      }
    },
    setConfirmationTrajectory: (state, action: PayloadAction<ConfirmationTrajectory | null>) => {
      state.confirmationTrajectory = action.payload;
    },
    setOpenConfirmPublishDraft: (state, action: PayloadAction<boolean>) => {
      state.confirmPublishDraft = action.payload;
    },
    setOpenConfirmUnPublishDraft: (state, action: PayloadAction<boolean>) => {
      state.confirmUnPublishDraft = action.payload;
    },
    setTrajectory: (state, action: PayloadAction<ITrajectoryDetail>) => {
      if (state.trajectory !== action.payload) {
        state.trajectory = action.payload;
      }
    },
    setCurrentBookId: (state, action: PayloadAction<string | undefined>) => {
      if (state.currentBookId !== action.payload) {
        state.currentBookId = action.payload;
      }
    },
    setCurrentChapterId: (state, action: PayloadAction<string | undefined>) => {
      if (state.currentChapterId !== action.payload) {
        state.currentChapterId = action.payload;
      }
    },
    setCurrentBlockId: (state, action: PayloadAction<string | undefined>) => {
      if (state.currentBlockId !== action.payload) {
        state.currentBlockId = action.payload;
      }
    },
    setCurrentSectionCode: (state, action: PayloadAction<TrajectorySectionCode>) => {
      if (state.currentSectionCode !== action.payload) {
        state.currentSectionCode = action.payload;
      }
    },
    setCurrentPageId: (state, action: PayloadAction<string>) => {
      if (state.currentPageId !== action.payload) {
        state.currentPageId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // #region lock trajectory
    builder.addCase(lockTrajectory.pending, (state) => {
      state.loading = true;
      state.error = undefined;
      state.success = undefined;
    });
    builder.addCase(lockTrajectory.fulfilled, (state, action) => {
      state.loading = false;
      state.lockStatus = action.payload.lockStatus;

      if (action.payload.lockStatus.locked) {
        if (action.payload.lockStatus.lockedBy?.id === action.payload.user?.id)
          state.viewMode = 'edit';

        const trajectoryId = state.currentTrajectoryId as string;
        state.timeoutAction = window.setTimeout(() => {
          store.dispatch(checkTrajectoryHasLock(trajectoryId));
        }, action.payload.lockStatus.timeLeft);
      } else {
        state.timeoutAction = undefined;
      }

      state.error = undefined;
      state.success = true;
    });
    builder.addCase(lockTrajectory.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.success = false;
    });
    // #endregion

    // #region unlock trajectory
    builder.addCase(unlockTrajectory.pending, (state) => {
      state.loading = true;
      state.error = undefined;
      state.success = undefined;
    });
    builder.addCase(unlockTrajectory.fulfilled, (state, action) => {
      state.loading = false;
      state.lockStatus = action.payload.lockStatus;
      if (state.timeoutAction) {
        clearInterval(state.timeoutAction);
      }
      state.viewMode = 'read';
      state.error = undefined;
      state.success = true;
    });
    builder.addCase(unlockTrajectory.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.success = false;
    });
    // #endregion

    // #region check trajectory lock
    builder.addCase(checkTrajectoryHasLock.pending, (state) => {
      state.loading = true;
      state.error = undefined;
      state.success = undefined;
    });
    builder.addCase(checkTrajectoryHasLock.fulfilled, (state, action) => {
      state.loading = false;
      state.lockStatus = action.payload.lockStatus;
      if (state.timeoutAction) clearInterval(state.timeoutAction);

      if (action.payload.lockStatus.locked) {
        if (action.payload.lockStatus.lockedBy?.id === action.payload.user?.id)
          state.viewMode = 'edit';

        const trajectoryId = state.currentTrajectoryId as string;
        state.timeoutAction = window.setTimeout(() => {
          store.dispatch(checkTrajectoryHasLock(trajectoryId));
        }, action.payload.lockStatus.timeLeft);
      } else {
        state.timeoutAction = undefined;
        state.viewMode = 'read';
      }

      state.error = undefined;
    });
    builder.addCase(checkTrajectoryHasLock.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    // #endregion
  },
});

export const {
  setCurrentTrajectoryId,
  setTrajectory,
  setCurrentBookId,
  setCurrentChapterId,
  setCurrentSectionCode,
  setCurrentBlockId,
  setCurrentPageId,
  setConfirmationTrajectory,
  setOpenConfirmPublishDraft,
  setOpenConfirmUnPublishDraft,
} = trajectorySlice.actions;

export const { reducer: trajectoriesReducer } = trajectorySlice;
